import { Box, IconButton, Typography } from '@mui/material'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoCircleIcon,
  Star01Icon,
  Trash03Icon,
} from '@untitled-theme/icons-react'
import { useEffect, useRef, useState } from 'react'

import { housePartsIcons } from '@/assets/icons/house-parts'
import {
  getRetrofitDisplayName,
  retrofitToIcon,
} from '@/features/measures-overview/components/MeasuresTable/MeasuresTable.types'
import EditISFPDataPopup from '@/shared/components/EditISFPDataPopup'
import { Energieklasse } from '@/shared/components/Energieklasse'
import { MaximumFundingInfoPopup, RenovationCostsInfoPopup } from '@/shared/components/InfoPopup'
import { PropertyValueIncrease } from '@/shared/components/PropertyValueIncrease'
import { SavingsPotential } from '@/shared/components/SavingsPotential'
import type { EBHubAssessment } from '@/shared/types'
import type { FirestoreCustomPackageDocument } from '@/shared/types/custom-package.types'

interface CustomPackagesProps {
  packages: FirestoreCustomPackageDocument[]
  selectedPackageId?: string
  assessment?: EBHubAssessment
  onSelectPackage: (packageId: string) => void
  isSelectingPackage?: boolean
  onDeletePackage: (packageId: string) => void
  isDeletingPackage?: boolean
}

const PACKAGE_ITEM_WIDTH = 340
const PACKAGE_GAP = 16

const CustomPackages = ({
  packages,
  selectedPackageId,
  assessment,
  onSelectPackage,
  isSelectingPackage,
  onDeletePackage,
  isDeletingPackage,
}: CustomPackagesProps) => {
  // Calculate max number of measures across all packages
  const maxMeasures = Math.max(...packages.map(pkg => pkg.measures.length), 0)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null)
  const [isRenovationCostsPopupOpen, setIsRenovationCostsPopupOpen] = useState(false)
  const [isMaxFundingPopupOpen, setIsMaxFundingPopupOpen] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    }
  }

  useEffect(() => {
    checkScroll()
    const handleResize = () => checkScroll()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [packages])

  // Clear deleting state when packages change (package was removed)
  useEffect(() => {
    if (deletingPackageId && !packages.find(pkg => pkg.id === deletingPackageId)) {
      setDeletingPackageId(null)
    }
  }, [packages, deletingPackageId])

  const handleDeleteClick = (packageId: string) => {
    setDeletingPackageId(packageId)
    onDeletePackage(packageId)
  }

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -(PACKAGE_ITEM_WIDTH + PACKAGE_GAP),
        behavior: 'smooth',
      })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: PACKAGE_ITEM_WIDTH + PACKAGE_GAP,
        behavior: 'smooth',
      })
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
      }}
    >
      {/* Left gradient */}
      {canScrollLeft && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '8px',
            background: 'linear-gradient(to right, white, transparent)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Right gradient */}
      {canScrollRight && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '8px',
            background: 'linear-gradient(to left, white, transparent)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Left scroll button */}
      {canScrollLeft && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            position: 'fixed',
            left: {
              xs: '28px',
              lg: 'calc(50% - 562.5px - 44px)',
            },
            top: 'calc(190px + (100vh - 190px) / 2)',
            transform: 'translateY(-50%)',
            zIndex: 101,
            backgroundColor: '#FFFFFF',
            width: '40px',
            height: '40px',
            boxShadow: '0px 3px 9px 0px #0000002E',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
          }}
        >
          <ChevronLeftIcon style={{ color: '#181D29' }} />
        </IconButton>
      )}

      {/* Right scroll button */}
      {canScrollRight && (
        <IconButton
          onClick={scrollRight}
          sx={{
            position: 'fixed',
            right: {
              xs: '28px',
              lg: 'calc(50% - 562.5px - 44px)',
            },
            top: 'calc(190px + (100vh - 190px) / 2)',
            transform: 'translateY(-50%)',
            zIndex: 101,
            backgroundColor: '#FFFFFF',
            width: '40px',
            height: '40px',
            boxShadow: '0px 3px 9px 0px #0000002E',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
          }}
        >
          <ChevronRightIcon style={{ color: '#181D29' }} />
        </IconButton>
      )}

      <Box
        ref={scrollContainerRef}
        onScroll={checkScroll}
        sx={{
          display: 'flex',
          gap: `${PACKAGE_GAP}px`,
          overflowX: 'auto',
          overflowY: 'auto',
          height: '100%',
          '&::-webkit-scrollbar': {
            height: '0px',
          },
        }}
      >
        {/* Custom package items will go here */}
        {packages.map((pkg, index) => {
          const isSelected = pkg.id === selectedPackageId
          const emptyItemsCount = maxMeasures - pkg.measures.length
          const isDeleting = deletingPackageId === pkg.id
          const isProcessing = pkg.status === 'processing'
          const isFailed = pkg.status === 'failure'

          const canSelectAsMainPackage =
            !isSelectingPackage && !isDeleting && !isProcessing && !isFailed
          const canDeletePackage = !isDeletingPackage && !isDeleting

          return (
            <Box
              key={pkg.id}
              sx={{
                position: 'relative',
                backgroundColor: '#FAFAFB',
                border: '1px solid #E1E1E4',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                width: `${PACKAGE_ITEM_WIDTH}px`,
                flexShrink: 0,
              }}
            >
              {/* Overlay when deleting */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '16px',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isDeleting ? 1 : 0,
                  pointerEvents: isDeleting ? 'auto' : 'none',
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#74777F',
                    backgroundColor: '#FFFFFF',
                    padding: '4px',
                    borderRadius: '8px',
                    boxShadow: '0 0 24px 24px white',
                  }}
                >
                  Wird gelöscht...
                </Typography>
              </Box>
              {/* Overlay when failed (Only shown when not deleting) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '16px',
                  zIndex: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: !isDeleting && isFailed ? 1 : 0,
                  pointerEvents: !isDeleting && isFailed ? 'auto' : 'none',
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    padding: '4px',
                    borderRadius: '8px',
                    boxShadow: '0 0 24px 24px white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#D32F2F',
                    }}
                  >
                    Erstellung fehlgeschlagen
                  </Typography>
                  <Typography
                    onClick={() => handleDeleteClick(pkg.id)}
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#D32F2F',
                      cursor: 'pointer',
                      '&:hover': {
                        color: '#B71C1C',
                      },
                    }}
                  >
                    Löschen
                  </Typography>
                </Box>
              </Box>
              {/* Overlay when processing (Only shown when not deleting) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '16px',
                  zIndex: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: !isDeleting && isProcessing ? 1 : 0,
                  pointerEvents: !isDeleting && isProcessing ? 'auto' : 'none',
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#74777F',
                    backgroundColor: '#FFFFFF',
                    padding: '4px',
                    borderRadius: '8px',
                    boxShadow: '0 0 24px 24px white',
                  }}
                >
                  Wird verarbeitet...
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '38px',
                  gap: 2,
                  margin: '13px 14px 18px 12px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Star01Icon
                    onClick={() => {
                      if (canSelectAsMainPackage) {
                        onSelectPackage(pkg.id)
                      }
                    }}
                    style={{
                      color: isSelected ? '#EDC74C' : '#181D29',
                      fill: isSelected ? '#EDC74C' : 'transparent',
                      cursor: canSelectAsMainPackage ? 'pointer' : 'default',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 600,
                    }}
                  >
                    Paket {String(index + 1).padStart(2, '0')}
                  </Typography>
                </Box>
                <Trash03Icon
                  onClick={() => {
                    if (canDeletePackage) {
                      handleDeleteClick(pkg.id)
                    }
                  }}
                  style={{
                    color: '#181D29',
                    cursor: canDeletePackage ? 'pointer' : 'default',
                    opacity: canDeletePackage ? 1 : 0.5,
                  }}
                />
              </Box>

              <Box
                sx={{
                  padding: '0 15px 22px 15px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Ausgewählte Sanierungsmaßnahmen
                </Typography>
              </Box>

              <Box
                sx={{
                  padding: '0 15px 36px 15px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '16px',
                }}
              >
                {pkg.measures.map(measureName => (
                  <Box
                    key={measureName}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Box
                      component="img"
                      src={housePartsIcons[retrofitToIcon[measureName]]}
                      alt={getRetrofitDisplayName(measureName)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        flexShrink: 0,
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getRetrofitDisplayName(measureName)}
                    </Typography>
                  </Box>
                ))}
                {/* Add empty items to maintain grid alignment */}
                {Array.from({ length: emptyItemsCount }).map((_, idx) => (
                  <Box
                    key={`empty-${idx}`}
                    sx={{
                      width: '32px',
                      height: '32px',
                      flexShrink: 0,
                      visibility: 'hidden',
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  padding: '0 15px 16px 15px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                  }}
                >
                  Auswirkung
                </Typography>
              </Box>

              <Box
                sx={{
                  padding: '0 12px 8px 12px',
                }}
              >
                <Energieklasse
                  currentRating={
                    assessment?.demandBasedAssessment.currentState.energetics.energyLabel || 'E'
                  }
                  potentialRating={pkg.data.demandBasedCalculation.energetics.energyLabel}
                />
              </Box>

              <Box
                sx={{
                  padding: '0 12px 8px 12px',
                }}
              >
                <SavingsPotential
                  avgEnergyCostSavingsWithinFiveYears={{
                    inCt: pkg.data.consumptionBasedCalculation.economics
                      .avgEnergyCostSavingsInCtWithinFiveYears,
                    inPct:
                      pkg.data.consumptionBasedCalculation.economics
                        .avgEnergyCostSavingsInPctWithinFiveYears,
                  }}
                  annualFinalEnergyInKwh={{
                    current:
                      assessment?.consumptionBasedAssessment.currentState.energetics
                        .annualFinalEnergyInKwh.total,
                    retrofit:
                      pkg.data.consumptionBasedCalculation.energetics.annualFinalEnergyInKwh.total,
                  }}
                />
              </Box>

              <Box
                sx={{
                  padding: '0 12px 8px 12px',
                }}
              >
                <PropertyValueIncrease
                  percentage={
                    pkg.data.demandBasedCalculation.economics.realEstateValuationIncreaseInPct
                  }
                  sx={{ padding: '16px 16px 8px 16px' }}
                />
              </Box>

              <Box
                sx={{
                  padding: '0 12px 12px 12px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E1E1E4',
                    borderRadius: '8px',
                    height: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                    }}
                  >
                    Amortisationszeit
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {(() => {
                      const years =
                        pkg.data.consumptionBasedCalculation.economics.amortizationTimeInYears
                      if (!years) return 'k. A.'
                      if (years > 40) return '+40 Jahre'
                      return `${years} Jahre`
                    })()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E1E1E4',
                    borderRadius: '8px',
                    height: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '8px',
                    position: 'relative',
                  }}
                >
                  <InfoCircleIcon
                    style={{
                      width: 18,
                      height: 18,
                      color: '#464A54',
                      position: 'absolute',
                      top: '6px',
                      right: '8px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsRenovationCostsPopupOpen(true)}
                  />
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                    }}
                  >
                    Sanierungskosten
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                      }}
                    >
                      Ab{' '}
                    </Typography>
                    {Math.round(
                      pkg.data.consumptionBasedCalculation.economics.retrofitCostsInCt.mean / 100
                    ).toLocaleString('de-DE')}{' '}
                    €
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E1E1E4',
                    borderRadius: '8px',
                    height: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '8px',
                    position: 'relative',
                  }}
                >
                  <InfoCircleIcon
                    style={{
                      width: 18,
                      height: 18,
                      color: '#464A54',
                      position: 'absolute',
                      top: '6px',
                      right: '8px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsMaxFundingPopupOpen(true)}
                  />
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                    }}
                  >
                    max. Förderung
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {Math.round(
                      (pkg.data.consumptionBasedCalculation.economics.retrofitCostsInCt.mean /
                        100) *
                        (pkg.data.consumptionBasedCalculation.economics.subsidyRateInPct / 100)
                    ).toLocaleString('de-DE')}{' '}
                    €
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E1E1E4',
                    borderRadius: '8px',
                    height: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                    }}
                  >
                    Kosten nach Förderung
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                      }}
                    >
                      Bis zu{' '}
                    </Typography>
                    {Math.round(
                      (pkg.data.consumptionBasedCalculation.economics.retrofitCostsInCt.mean /
                        100) *
                        (1 - pkg.data.consumptionBasedCalculation.economics.subsidyRateInPct / 100)
                    ).toLocaleString('de-DE')}{' '}
                    €
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>

      <RenovationCostsInfoPopup
        open={isRenovationCostsPopupOpen}
        onClose={() => setIsRenovationCostsPopupOpen(false)}
        onEdit={() => {
          setIsRenovationCostsPopupOpen(false)
          setIsEditPopupOpen(true)
        }}
      />

      <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />
      <MaximumFundingInfoPopup
        open={isMaxFundingPopupOpen}
        onClose={() => setIsMaxFundingPopupOpen(false)}
      />
    </Box>
  )
}

export default CustomPackages
